import { useState, useEffect } from 'react'
import { useDraggable } from '@dnd-kit/core'
import { savedBlocksApi, ApiSavedBlock } from '../../utils/api'
import { useEditorStore } from '../../store/editor.store'

function SavedBlockItem({ block, onDelete }: { block: ApiSavedBlock; onDelete: () => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `saved-${block.id}`,
    data: { type: 'new-block', blockType: block.blockType, savedBlockData: block.data },
  })

  function handleClick() {
    const { page, selectedSectionId, addSection, addBlock } = useEditorStore.getState()
    const targetSection = selectedSectionId
      ? page.sections.find(s => s.id === selectedSectionId)
      : page.sections[page.sections.length - 1]

    if (targetSection) {
      addBlock(targetSection.id, block.blockType, block.data)
    } else {
      addSection()
      setTimeout(() => {
        const { page: p, addBlock: ab } = useEditorStore.getState()
        const sec = p.sections[p.sections.length - 1]
        if (sec) ab(sec.id, block.blockType, block.data)
      }, 0)
    }
  }

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={handleClick}
      style={{
        opacity: isDragging ? 0.5 : 1,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '8px 10px',
        background: 'var(--bg-2)',
        borderRadius: '8px',
        cursor: 'grab',
        border: '1px solid var(--border)',
        transition: 'border-color 0.15s',
        userSelect: 'none',
      }}
    >
      <span style={{ fontSize: '18px', flexShrink: 0 }}>{block.icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{block.name}</div>
        <div style={{ fontSize: '10px', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{block.blockType}</div>
      </div>
      <button
        onClick={e => { e.stopPropagation(); if (confirm('Удалить компонент?')) onDelete() }}
        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: 'var(--text-muted)', padding: '2px 6px', flexShrink: 0, lineHeight: 1 }}
        title="Удалить"
      >×</button>
    </div>
  )
}

export function SavedBlocksPanel() {
  const [blocks, setBlocks] = useState<ApiSavedBlock[]>([])
  const [loading, setLoading] = useState(true)

  const load = () => {
    setLoading(true)
    savedBlocksApi.list().then(setBlocks).catch(() => setBlocks([])).finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  const handleDelete = async (id: string) => {
    await savedBlocksApi.delete(id).catch(() => {})
    load()
  }

  if (loading) {
    return <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>Загрузка...</div>
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ fontSize: '11px', color: 'var(--text-muted)', lineHeight: 1.4 }}>
          Сохранённые блоки — нажмите или перетащите на холст. Чтобы сохранить, нажмите <strong>📌 Сохранить как компонент</strong> в панели свойств блока.
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {blocks.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 16px', color: 'var(--text-muted)', fontSize: '12px' }}>
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>📌</div>
            <div style={{ fontWeight: 600, marginBottom: '4px' }}>Нет сохранённых компонентов</div>
            <div>Выберите блок на холсте и нажмите «Сохранить как компонент»</div>
          </div>
        ) : (
          blocks.map(b => (
            <SavedBlockItem key={b.id} block={b} onDelete={() => handleDelete(b.id)} />
          ))
        )}
      </div>

      {blocks.length > 0 && (
        <div style={{ padding: '8px 12px', borderTop: '1px solid var(--border)', fontSize: '10px', color: 'var(--text-muted)', textAlign: 'center' }}>
          {blocks.length} сохранённых компонентов
        </div>
      )}
    </div>
  )
}
